// src/services/moderationDataService.js
import { FirebaseFirestore } from "@capacitor-firebase/firestore";
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
  query,
  orderBy,
} from "firebase/firestore";

import { db } from "../firebase";

import {
  isNativeFirestorePlatform,
  extractNativeDocuments,
  extractNativeDocumentData,
} from "./firestorePlatform";

const USERS_COLLECTION = "nutzer";
const REPORTS_COLLECTION = "moderationReports";

const normalizeObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const makeClientTimestamp = () => new Date().toISOString();

const blockedUsersCollectionPath = (myUid) =>
  `${USERS_COLLECTION}/${myUid}/blockedUsers`;

const blockedUserDocPath = (myUid, blockedUserId) =>
  `${USERS_COLLECTION}/${myUid}/blockedUsers/${blockedUserId}`;

const reportDocPath = (reportId) => `${REPORTS_COLLECTION}/${reportId}`;

const normalizeBlockedUser = (id, data = {}) => {
  const d = normalizeObject(data);

  return {
    id,
    blockedUserId: d.blockedUserId || id,
    blockedUserName: d.blockedUserName || "",
    blockedBy: d.blockedBy || "",

    sourceContentType: d.sourceContentType || "",
    sourceContentId: d.sourceContentId || null,

    createdAt: d.createdAt || null,
    createdAtClient: d.createdAtClient || null,
    updatedAt: d.updatedAt || null,
    updatedAtClient: d.updatedAtClient || null,
  };
};

const normalizeReport = (id, data = {}) => {
  const d = normalizeObject(data);

  return {
    id,

    contentType: d.contentType || "content",
    contentId: d.contentId || null,
    parentId: d.parentId || null,

    ownerId: d.ownerId || null,
    ownerName: d.ownerName || "",

    reporterId: d.reporterId || null,
    reporterEmail: d.reporterEmail || "",

    reason: d.reason || "",
details: d.details || d.reason || "",

contentSnapshot: {
  title: d.contentSnapshot?.title || "",
  text: d.contentSnapshot?.text || "",
  url: d.contentSnapshot?.url || "",
},

status: d.status || "open",

emailNotified: d.emailNotified === true,
emailNotifiedReason: d.emailNotifiedReason || null,
emailNotifiedAt: d.emailNotifiedAt || null,

createdAt: d.createdAt || null,
    createdAtClient: d.createdAtClient || null,
    updatedAt: d.updatedAt || null,
    updatedAtClient: d.updatedAtClient || null,

    extra: normalizeObject(d.extra),
  };
};

const extractNativeCollectionDocs = (payload) => {
  const docs = extractNativeDocuments(payload);
  return Array.isArray(docs) ? docs : [];
};

const getNativeDocId = (item, fallback = "") => {
  return (
    item?.id ||
    item?.documentId ||
    item?.snapshot?.id ||
    item?.document?.id ||
    (typeof item?.reference === "string"
      ? item.reference.split("/").pop()
      : null) ||
    fallback
  );
};

const getNativeDocData = (item) => {
  return (
    item?.data ||
    item?.snapshot?.data ||
    item?.document?.data ||
    item?.value ||
    item?.fields ||
    {}
  );
};

const parseDateValue = (value) => {
  if (!value) return 0;

  if (typeof value === "number") return value;

  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  if (typeof value.toMillis === "function") {
    return value.toMillis();
  }

  if (typeof value.seconds === "number") {
    return value.seconds * 1000;
  }

  return 0;
};

const sortByNewestClientDate = (a, b) => {
  const at =
    parseDateValue(a?.createdAtClient) ||
    parseDateValue(a?.createdAt) ||
    parseDateValue(a?.updatedAtClient) ||
    parseDateValue(a?.updatedAt);

  const bt =
    parseDateValue(b?.createdAtClient) ||
    parseDateValue(b?.createdAt) ||
    parseDateValue(b?.updatedAtClient) ||
    parseDateValue(b?.updatedAt);

  return bt - at;
};

/**
 * Meldung erstellen
 *
 * Firestore:
 * moderationReports/{reportId}
 *
 * Später kann eine Cloud Function auf neue Reports reagieren
 * und automatisch eine E-Mail an info@edens-ernte.app senden.
 */
export const createModerationReportData = async ({
  contentType = "content",
  contentId = null,
  parentId = null,

  ownerId = null,
  ownerName = "",

  reporterId = null,
  reporterEmail = "",

  reason = "",
  details = "",
  contentSnapshot = {},
  extra = {},
} = {}) => {
  if (!reporterId) {
    throw new Error("createModerationReportData: reporterId fehlt");
  }

  const nowClient = makeClientTimestamp();

 const payload = {
  contentType: contentType || "content",
  contentId: contentId || null,
  parentId: parentId || null,

  ownerId: ownerId || null,
  ownerName: ownerName || "",

  reporterId,
  reporterEmail: reporterEmail || "",

  reason: reason || "",
  details: details || reason || "",

  contentSnapshot: {
    title: contentSnapshot?.title || "",
    text: contentSnapshot?.text || "",
    url: contentSnapshot?.url || "",
  },

  status: "open",

  extra: normalizeObject(extra),

  createdAtClient: nowClient,
  updatedAtClient: nowClient,
};

  if (isNativeFirestorePlatform()) {
    const reportId = `report_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 10)}`;

    await FirebaseFirestore.setDocument({
      reference: reportDocPath(reportId),
      data: payload,
      merge: false,
    });

    return reportId;
  }

  const refDoc = await addDoc(collection(db, REPORTS_COLLECTION), {
    ...payload,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return refDoc.id;
};

/**
 * Kürzerer Alias für ContentActionMenu.
 */
export const createReportData = createModerationReportData;

/**
 * Einzelne Meldung laden.
 * Optional für spätere AdminPage.
 */
export const getModerationReportByIdData = async (reportId) => {
  if (!reportId) return null;

  if (isNativeFirestorePlatform()) {
    const result = await FirebaseFirestore.getDocument({
      reference: reportDocPath(reportId),
    });

    const data = extractNativeDocumentData(result);
    if (!data || Object.keys(data).length === 0) return null;

    return normalizeReport(reportId, data);
  }

  const snap = await getDoc(doc(db, REPORTS_COLLECTION, reportId));
  if (!snap.exists()) return null;

  return normalizeReport(snap.id, snap.data());
};

/**
 * Meldungen laden.
 * Optional für spätere AdminPage.
 */
export const getModerationReportsData = async () => {
  if (isNativeFirestorePlatform()) {
    const result = await FirebaseFirestore.getCollection({
      reference: REPORTS_COLLECTION,
    });

    const docs = extractNativeCollectionDocs(result);

    return docs
      .map((item, index) => {
        const id = getNativeDocId(item, `report-${index}`);
        const data = getNativeDocData(item);
        return normalizeReport(id, data);
      })
      .sort(sortByNewestClientDate);
  }

  try {
    const q = query(
      collection(db, REPORTS_COLLECTION),
      orderBy("createdAtClient", "desc")
    );

    const snap = await getDocs(q);
    return snap.docs.map((d) => normalizeReport(d.id, d.data()));
  } catch (error) {
    console.warn(
      "getModerationReportsData: orderBy fehlgeschlagen, nutze Fallback:",
      error
    );

    const snap = await getDocs(collection(db, REPORTS_COLLECTION));

    return snap.docs
      .map((d) => normalizeReport(d.id, d.data()))
      .sort(sortByNewestClientDate);
  }
};

/**
 * Nutzer blockieren
 *
 * Firestore:
 * nutzer/{myUid}/blockedUsers/{blockedUserId}
 *
 * Kompatibel mit:
 * - myUid
 * - currentUserId
 * - userId
 */
export const blockUserData = async ({
  myUid,
  currentUserId,
  userId,

  blockedUserId,
  blockedUserName = "",

  sourceContentType = "",
  sourceContentId = null,

  contentType = "",
  contentId = null,
} = {}) => {
  const blockerUid = myUid || currentUserId || userId;

  if (!blockerUid) {
    throw new Error("blockUserData: myUid/currentUserId fehlt");
  }

  if (!blockedUserId) {
    throw new Error("blockUserData: blockedUserId fehlt");
  }

  if (String(blockerUid) === String(blockedUserId)) {
    throw new Error("blockUserData: Du kannst dich nicht selbst blockieren");
  }

  const nowClient = makeClientTimestamp();

  const payload = {
    blockedUserId,
    blockedUserName: blockedUserName || "",
    blockedBy: blockerUid,

    sourceContentType: sourceContentType || contentType || "",
    sourceContentId: sourceContentId || contentId || null,

    createdAtClient: nowClient,
    updatedAtClient: nowClient,
  };

  if (isNativeFirestorePlatform()) {
    await FirebaseFirestore.setDocument({
      reference: blockedUserDocPath(blockerUid, blockedUserId),
      data: payload,
      merge: true,
    });

    return blockedUserId;
  }

  await setDoc(
    doc(db, USERS_COLLECTION, blockerUid, "blockedUsers", blockedUserId),
    {
      ...payload,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

  return blockedUserId;
};

/**
 * Blockierte Nutzer laden.
 *
 * Wird für ProfileOptionsPage/ProfileAction genutzt.
 */
export const getBlockedUsersData = async (myUid) => {
  if (!myUid) return [];

  if (isNativeFirestorePlatform()) {
    const result = await FirebaseFirestore.getCollection({
      reference: blockedUsersCollectionPath(myUid),
    });

    const docs = extractNativeCollectionDocs(result);

    return docs
      .map((item, index) => {
        const id = getNativeDocId(item, `blocked-${index}`);
        const data = getNativeDocData(item);
        return normalizeBlockedUser(id, data);
      })
      .sort(sortByNewestClientDate);
  }

  try {
    const q = query(
      collection(db, USERS_COLLECTION, myUid, "blockedUsers"),
      orderBy("createdAtClient", "desc")
    );

    const snap = await getDocs(q);
    return snap.docs.map((d) => normalizeBlockedUser(d.id, d.data()));
  } catch (error) {
    console.warn(
      "getBlockedUsersData: orderBy fehlgeschlagen, nutze Fallback:",
      error
    );

    const snap = await getDocs(
      collection(db, USERS_COLLECTION, myUid, "blockedUsers")
    );

    return snap.docs
      .map((d) => normalizeBlockedUser(d.id, d.data()))
      .sort(sortByNewestClientDate);
  }
};

/**
 * Nutzer entsperren.
 */
export const unblockUserData = async (myUid, blockedUserId) => {
  if (!myUid) {
    throw new Error("unblockUserData: myUid fehlt");
  }

  if (!blockedUserId) {
    throw new Error("unblockUserData: blockedUserId fehlt");
  }

  if (isNativeFirestorePlatform()) {
    await FirebaseFirestore.deleteDocument({
      reference: blockedUserDocPath(myUid, blockedUserId),
    });

    return true;
  }

  await deleteDoc(
    doc(db, USERS_COLLECTION, myUid, "blockedUsers", blockedUserId)
  );

  return true;
};

/**
 * Nur IDs laden, damit man später Listen filtern kann.
 */
export const getBlockedUserIdsData = async (myUid) => {
  const list = await getBlockedUsersData(myUid);
  return list.map((entry) => entry.blockedUserId).filter(Boolean);
};

/**
 * Prüfen, ob ein bestimmter Nutzer blockiert ist.
 */
export const isUserBlockedData = async (myUid, otherUserId) => {
  if (!myUid || !otherUserId) return false;

  if (String(myUid) === String(otherUserId)) return false;

  if (isNativeFirestorePlatform()) {
    const result = await FirebaseFirestore.getDocument({
      reference: blockedUserDocPath(myUid, otherUserId),
    });

    const data = extractNativeDocumentData(result);
    return !!data && Object.keys(data).length > 0;
  }

  const snap = await getDoc(
    doc(db, USERS_COLLECTION, myUid, "blockedUsers", otherUserId)
  );

  return snap.exists();
};

/**
 * Helper für spätere Filterung.
 *
 * Kann in ForumsPage, ForumMsgPage, BrowsePage etc. genutzt werden.
 */
/**
 * Universeller Owner/User-ID Finder.
 * Funktioniert für Angebote, Beiträge, Antworten, Nachrichten usw.
 */
export const getContentOwnerId = (item = {}) => {
  if (!item || typeof item !== "object") return "";

  return String(
    item.userId ||
      item.authorId ||
      item.ownerId ||
      item.createdBy ||
      item.createdByUid ||
      item.createdByUserId ||
      item.anbieterId ||
      item.sellerId ||
      item.senderId ||
      item.fromUid ||
      item.uid ||

      // ✅ Angebote / Verkäuferdaten
      item?.seller?.uid ||
      item?.seller?.userId ||
      item?.seller?.ownerId ||
      item?.seller?.sellerId ||
      item?.seller?.id ||

      // ✅ falls später andere Strukturen auftauchen
      item?.anbieter?.uid ||
      item?.anbieter?.userId ||
      item?.anbieter?.ownerId ||
      item?.owner?.uid ||
      item?.owner?.userId ||
      item?.user?.uid ||
      item?.user?.id ||
      ""
  ).trim();
};

/**
 * Universeller Filter:
 * Blendet Inhalte aus, deren Besitzer blockiert wurde.
 */
export const filterBlockedOwners = (items = [], blockedUserIds = []) => {
  if (!Array.isArray(items)) return [];

  if (!Array.isArray(blockedUserIds) || blockedUserIds.length === 0) {
    return items;
  }

  const blockedSet = new Set(blockedUserIds.map(String));

  return items.filter((item) => {
    const ownerId = getContentOwnerId(item);

    // Wenn kein Besitzer erkannt wird, Inhalt NICHT ausblenden.
    return !ownerId || !blockedSet.has(String(ownerId));
  });
};

/**
 * Helper speziell für Nachrichten/Threads.
 *
 * Entfernt Threads, bei denen der Peer blockiert ist.
 */
export const filterBlockedThreads = (threads = [], blockedUserIds = []) => {
  if (!Array.isArray(threads)) return [];
  if (!Array.isArray(blockedUserIds) || blockedUserIds.length === 0) {
    return threads;
  }

  const blockedSet = new Set(blockedUserIds.map(String));

  return threads.filter((thread) => {
    const peerId = thread?.peerId || thread?.id || "";
    if (!peerId) return true;

    return !blockedSet.has(String(peerId));
  });
};

/**
 * Optional für spätere AdminPage.
 */
export const normalizeModerationReportData = normalizeReport;
export const normalizeBlockedUserData = normalizeBlockedUser;